import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  Progress,
  useColorModeValue,
  Collapse,
} from '@chakra-ui/react';
import {
  FaUpload,
  FaMagic,
  FaCheck,
  FaTimes,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import {
  createScambio,
  searchScryfall,
  searchScryfallAdvanced,
} from '../../utils/scambiApi';

export default function BulkImportModal({ isOpen, onClose, onCardsAdded }) {
  const [deckList, setDeckList] = useState('');
  const [parsedCards, setParsedCards] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [showFormatInfo, setShowFormatInfo] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parseSuccess, setParseSuccess] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [retryMessage, setRetryMessage] = useState(null);
  const [importCompleted, setImportCompleted] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'cerco',
    lingua: 'it',
    set_name: '',
    condizioni: 'NM',
    note: '',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');

  // Funzione per verificare la corrispondenza perfetta tra carta parsata e risultato Scryfall
  const verifyCardMatch = (scryfallCard, parsedCard) => {
    // Verifica nome (deve essere esatto)
    if (scryfallCard.name !== parsedCard.name) return false;

    // Se abbiamo set code, verifica che corrisponda
    if (parsedCard.setCode && scryfallCard.set !== parsedCard.setCode)
      return false;

    // Se abbiamo collector number, verifica che corrisponda
    if (
      parsedCard.collectorNumber &&
      scryfallCard.collector_number !== parsedCard.collectorNumber
    )
      return false;

    return true;
  };

  // Funzione per cercare la carta su Scryfall con strategia intelligente
  const findCardOnScryfall = async (card) => {
    try {
      let scryfallData = null;
      let searchMethod = 'none';
      const errorDetails = [];

      // Strategia 1: Ricerca avanzata con set code e collector number (più precisa)
      if (card.setCode && card.collectorNumber) {
        try {
          const advancedResults = await searchScryfallAdvanced(
            card.name,
            card.setCode,
            card.collectorNumber
          );

          if (advancedResults && advancedResults.length > 0) {
            // Cerca la corrispondenza perfetta
            scryfallData = advancedResults.find((result) =>
              verifyCardMatch(result, card)
            );
            if (scryfallData) {
              searchMethod = 'advanced_perfect';
              return { scryfallData, searchMethod };
            }
            // Se non trova corrispondenza perfetta, usa il primo risultato
            [scryfallData] = advancedResults;
            searchMethod = 'advanced_first';
            return { scryfallData, searchMethod };
          }
        } catch (advancedError) {
          const errorMsg = `Ricerca avanzata fallita per ${card.name}: ${advancedError.message}`;
          errorDetails.push(errorMsg);
        }
      }

      // Strategia 2: Ricerca avanzata solo con set code
      if (card.setCode && !scryfallData) {
        try {
          const setResults = await searchScryfallAdvanced(
            card.name,
            card.setCode
          );

          if (setResults && setResults.length > 0) {
            // Cerca la corrispondenza perfetta
            scryfallData = setResults.find((result) =>
              verifyCardMatch(result, card)
            );
            if (scryfallData) {
              searchMethod = 'set_perfect';
              return { scryfallData, searchMethod };
            }
            // Se non trova corrispondenza perfetta, usa il primo risultato
            [scryfallData] = setResults;
            searchMethod = 'set_first';
            return { scryfallData, searchMethod };
          }
        } catch (setError) {
          const errorMsg = `Ricerca per set fallita per ${card.name}: ${setError.message}`;
          errorDetails.push(errorMsg);
        }
      }

      // Strategia 3: Ricerca generica per nome (fallback)
      if (!scryfallData) {
        try {
          const genericResults = await searchScryfall(card.name);

          if (genericResults && genericResults.length > 0) {
            // Cerca la corrispondenza perfetta se abbiamo set code o collector number
            if (card.setCode || card.collectorNumber) {
              scryfallData = genericResults.find((result) =>
                verifyCardMatch(result, card)
              );
              if (scryfallData) {
                searchMethod = 'generic_perfect';
                return { scryfallData, searchMethod };
              }
            }
            // Usa il primo risultato generico
            [scryfallData] = genericResults;
            searchMethod = 'generic_first';
            return { scryfallData, searchMethod };
          }
        } catch (genericError) {
          const errorMsg = `Ricerca generica fallita per ${card.name}: ${genericError.message}`;
          errorDetails.push(errorMsg);
        }
      }

      // Se arriviamo qui, nessuna strategia ha funzionato
      return { scryfallData: null, searchMethod: 'none', errorDetails };
    } catch (error) {
      const errorMsg = `Errore generale nella ricerca per ${card.name}: ${error.message}`;
      return {
        scryfallData: null,
        searchMethod: 'none',
        errorDetails: [errorMsg],
      };
    }
  };

  // Parsa la lista carte e estrae le carte
  const parseDeckList = (text) => {
    const lines = text.split('\n').filter((line) => line.trim());
    let currentSection = 'main'; // 'main' o 'sideboard'

    return lines.reduce((cards, line) => {
      const trimmedLine = line.trim();

      // Controlla se è un header di sezione
      if (trimmedLine.toLowerCase() === 'sideboard') {
        currentSection = 'sideboard';
        return cards;
      }

      // Salta linee vuote o header non utili
      if (
        !trimmedLine ||
        trimmedLine.toLowerCase() === 'deck' ||
        trimmedLine.toLowerCase() === 'about' ||
        trimmedLine.toLowerCase().includes('wants') ||
        trimmedLine.toLowerCase().includes('name')
      ) {
        return cards;
      }

      // Pattern per estrarre quantità, nome carta, set code, collector number e foil
      // Supporta formati come:
      // 1 Vito, Thorn of the Dusk Rose (PM21) 127p
      // 1 Bolas's Citadel (WAR) 79
      // 1 Lightning Bolt (M10) 133 *F*
      const match = trimmedLine.match(
        /^(\d+)\s+(.+?)(?:\s+\(([^)]+)\)\s+(\d+[a-z]?))?(?:\s+\*F\*)?$/
      );
      if (match) {
        const quantity = parseInt(match[1], 10);
        const cardName = match[2].trim();
        const setCode = match[3] || null; // Set code tra parentesi
        const collectorNumber = match[4] || null; // Collector number
        const isFoil = trimmedLine.includes('*F*'); // Controlla se è foil

        // Aggiungi la carta per ogni quantità
        const newCards = Array.from({ length: quantity }, () => ({
          name: cardName,
          section: currentSection,
          originalLine: trimmedLine,
          setCode,
          collectorNumber,
          isFoil,
          status: 'pending', // 'pending', 'success', 'error'
          error: null,
        }));

        return [...cards, ...newCards];
      }

      return cards;
    }, []);
  };

  const handleParseDeckList = () => {
    if (!deckList.trim()) {
      setParseError('Incolla una lista carte per iniziare');
      return;
    }

    const cards = parseDeckList(deckList);
    setParsedCards(cards);
    setParseError(null);
    setImportCompleted(false); // Reset dello stato di importazione completata

    if (cards.length === 0) {
      setParseSuccess(null);
      setParseError('Nessuna carta trovata nella lista. Verifica il formato.');
    } else {
      setParseSuccess(`Trovate ${cards.length} carte da importare`);
    }
  };

  const handleImportCards = async () => {
    if (parsedCards.length === 0) return;

    setImporting(true);
    setImportProgress(0);

    let successCount = 0;
    let errorCount = 0;

    // Processa le carte in sequenza per mantenere l'ordine
    await Promise.allSettled(
      parsedCards.map(async (card, index) => {
        try {
          // Cerca la carta su Scryfall con strategia intelligente
          const { scryfallData, searchMethod, errorDetails } =
            await findCardOnScryfall(card);

          // Aggiorna lo stato per mostrare il metodo di ricerca utilizzato
          setParsedCards((prev) =>
            prev.map((c, idx) =>
              idx === index
                ? {
                    ...c,
                    scryfallFound: !!scryfallData,
                    searchMethod,
                    errorDetails: errorDetails || [],
                  }
                : c
            )
          );

          // Crea lo scambio per questa carta
          await createScambio({
            nome: card.name,
            tipo: formData.tipo,
            lingua: formData.lingua,
            set_name: formData.set_name || scryfallData?.set_name || null,
            foil: card.isFoil || formData.foil, // Usa il foil dalla lista carte se disponibile
            condizioni: formData.condizioni,
            note: formData.note || '',

            // Usa i dati Scryfall se disponibili, altrimenti valori di default
            cmc: scryfallData?.cmc || null,
            set: scryfallData?.set || null,
            uri: scryfallData?.uri || null,
            fonte: scryfallData ? 'scryfall' : 'manual',
            power: scryfallData?.power || null,
            colors: scryfallData?.colors || [],
            prices: scryfallData?.prices || {},
            rarity: scryfallData?.rarity || null,
            mana_cost: scryfallData?.mana_cost || null,
            toughness: scryfallData?.toughness || null,
            type_line: scryfallData?.type_line || null,
            image_uris: scryfallData?.image_uris || {},
            legalities: scryfallData?.legalities || {},
            scryfallId: scryfallData?.id || null,
            oracle_text: scryfallData?.oracle_text || null,
            scryfall_uri: scryfallData?.scryfall_uri || null,
            color_identity: scryfallData?.color_identity || [],
            collector_number:
              card.collectorNumber || scryfallData?.collector_number || null,
          });

          // Aggiorna lo stato della carta
          setParsedCards((prev) =>
            prev.map((c, idx) =>
              idx === index ? { ...c, status: 'success' } : c
            )
          );

          successCount += 1;
          return { success: true, index };
        } catch (error) {
          // Aggiorna lo stato della carta con l'errore
          setParsedCards((prev) =>
            prev.map((c, idx) =>
              idx === index
                ? { ...c, status: 'error', error: error.message }
                : c
            )
          );

          errorCount += 1;
          return { success: false, index, error: error.message };
        }
      })
    );

    // Aggiorna il progresso
    setImportProgress(100);

    setImporting(false);

    // Mostra il risultato finale
    setImportResult({
      success: errorCount === 0,
      message: `${successCount} carte importate con successo${errorCount > 0 ? `, ${errorCount} errori` : ''}`,
      successCount,
      errorCount,
    });

    // Chiama la callback per aggiornare la lista
    if (successCount > 0) {
      onCardsAdded();
      setImportCompleted(true);
      setParseSuccess(null); // Nasconde il messaggio "Trovate X carte da importare"
    }
  };

  const handleClose = () => {
    setDeckList('');
    setParsedCards([]);
    setImportProgress(0);
    setParseError(null);
    setParseSuccess(null);
    setImportResult(null);
    setRetryMessage(null);
    setImportCompleted(false);
    onClose();
  };

  // Funzione per riprovare la ricerca di una carta specifica
  const handleRetryCard = async (card, index) => {
    try {
      // Resetta lo stato della carta
      setParsedCards((prev) =>
        prev.map((c, idx) =>
          idx === index
            ? { ...c, status: 'pending', error: null, errorDetails: [] }
            : c
        )
      );

      // Riprova la ricerca
      const { scryfallData, searchMethod, errorDetails } =
        await findCardOnScryfall(card);

      if (scryfallData) {
        // Se ora trova la carta, aggiorna lo stato
        setParsedCards((prev) =>
          prev.map((c, idx) =>
            idx === index
              ? {
                  ...c,
                  scryfallFound: true,
                  searchMethod,
                  errorDetails: errorDetails || [],
                }
              : c
          )
        );

        setRetryMessage({
          type: 'success',
          title: 'Ricerca riuscita!',
          message: `${card.name} trovata su Scryfall`,
        });
      } else {
        // Se ancora non trova, mostra gli errori
        setParsedCards((prev) =>
          prev.map((c, idx) =>
            idx === index
              ? {
                  ...c,
                  scryfallFound: false,
                  searchMethod: 'none',
                  errorDetails: errorDetails || [],
                }
              : c
          )
        );

        setRetryMessage({
          type: 'warning',
          title: 'Ricerca fallita',
          message: `${card.name} non trovata su Scryfall`,
        });
      }
    } catch (error) {
      setRetryMessage({
        type: 'error',
        title: 'Errore nel retry',
        message: `Impossibile riprovare la ricerca per ${card.name}`,
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <Icon as={FaCheck} color="green.500" />;
      case 'error':
        return <Icon as={FaTimes} color="red.500" />;
      default:
        return <Icon as={FaMagic} color="gray.400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'error':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Funzione per ottenere il colore del badge in base al metodo di ricerca
  const getSearchMethodColor = (searchMethod) => {
    switch (searchMethod) {
      case 'advanced_perfect':
        return 'green';
      case 'advanced_first':
        return 'green';
      case 'set_perfect':
        return 'teal';
      case 'set_first':
        return 'cyan';
      case 'generic_perfect':
        return 'orange';
      case 'generic_first':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  // Funzione per ottenere l'etichetta del badge in base al metodo di ricerca
  const getSearchMethodLabel = (searchMethod) => {
    switch (searchMethod) {
      case 'advanced_perfect':
        return '🎯 Perfetto';
      case 'advanced_first':
        return '🎯 Perfetto';
      case 'set_perfect':
        return '🎯 Set';
      case 'set_first':
        return '🔍 Set';
      case 'generic_perfect':
        return '🎯 Nome';
      case 'generic_first':
        return '🔍 Nome';
      default:
        return '❌ N/A';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FaUpload} color="blue.500" />
            <Text>Importazione Bulk Carte</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Passaggi da seguire */}
            <VStack spacing={3} align="stretch">
              <Alert status="success" borderRadius="md">
                <Box>
                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    color="green.700"
                    mb={3}
                  >
                    📋 Passaggi per l&apos;Importazione
                  </Text>

                  <VStack spacing={3} align="start">
                    <HStack spacing={3} align="start">
                      <Badge
                        colorScheme="green"
                        variant="solid"
                        fontSize="sm"
                        minW="24px"
                        textAlign="center"
                      >
                        1
                      </Badge>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="green.700"
                        >
                          Incolla la Lista Carte
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Copia e incolla la tua lista carte nel campo sotto. Il
                          formato viene riconosciuto automaticamente.
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing={3} align="start">
                      <Badge
                        colorScheme="blue"
                        variant="solid"
                        fontSize="sm"
                        minW="24px"
                        textAlign="center"
                      >
                        2
                      </Badge>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="blue.700"
                        >
                          Clicca su &ldquo;Analizza Lista&rdquo;
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Il sistema analizza la lista e identifica tutte le
                          carte. Controlla i risultati prima di procedere.
                        </Text>
                      </VStack>
                    </HStack>

                    <HStack spacing={3} align="start">
                      <Badge
                        colorScheme="purple"
                        variant="solid"
                        fontSize="sm"
                        minW="24px"
                        textAlign="center"
                      >
                        3
                      </Badge>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="purple.700"
                        >
                          Clicca su &ldquo;Importa&rdquo;
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Ogni carta viene cercata automaticamente su Scryfall e
                          creata come scambio. Monitora il progresso!
                        </Text>
                      </VStack>
                    </HStack>
                  </VStack>
                </Box>
              </Alert>
            </VStack>

            {/* Formato Supportato - Dropdown */}
            <VStack spacing={3} align="stretch">
              <Alert status="info" borderRadius="md">
                <Box>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFormatInfo(!showFormatInfo)}
                    rightIcon={
                      showFormatInfo ? <FaChevronUp /> : <FaChevronDown />
                    }
                    color="blue.700"
                    fontWeight="medium"
                    p={0}
                    h="auto"
                    minH="auto"
                  >
                    Formato Supportato
                  </Button>

                  <Collapse in={showFormatInfo} animateOpacity>
                    <VStack spacing={2} align="start" mt={3}>
                      <Text fontSize="sm" fontWeight="medium" color="blue.700">
                        📋 Formato Completo (Raccomandato):
                      </Text>
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        bg="gray.100"
                        p={2}
                        borderRadius="md"
                      >
                        2 Bolas&apos;s Citadel (WAR) 79
                        <br />1 Vito, Thorn of the Dusk Rose (P30A) 28 *F*
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        → Set e art corretta, collector number preciso
                      </Text>

                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color="blue.700"
                        mt={2}
                      >
                        📝 Formato Semplice:
                      </Text>
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        bg="gray.100"
                        p={2}
                        borderRadius="md"
                      >
                        2 Bolas&apos;s Citadel
                        <br />1 Vito, Thorn of the Dusk Rose
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        → Prima versione disponibile (modificabile dopo)
                      </Text>

                      <Text fontSize="xs" color="gray.500" mt={2}>
                        💡 Gli header come &ldquo;About&rdquo;, &ldquo;Name
                        wants&rdquo;, &ldquo;Deck&rdquo; vengono automaticamente
                        filtrati.
                      </Text>
                    </VStack>
                  </Collapse>
                </Box>
              </Alert>
            </VStack>

            {/* Input per la lista carte */}
            <FormControl>
              <FormLabel fontWeight="bold" color="gray.700">
                Incolla la Lista Carte
              </FormLabel>
              <Textarea
                value={deckList}
                onChange={(e) => setDeckList(e.target.value)}
                placeholder={`Esempio Formato Completo:
 2 Bolas's Citadel (WAR) 79
1 Vito, Thorn of the Dusk Rose (P30A) 28 *F*`}
                rows={8}
                fontFamily="monospace"
                fontSize="sm"
              />
            </FormControl>

            {/* Opzioni di Importazione */}
            <Box
              p={4}
              bg="blue.50"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Text fontSize="sm" fontWeight="medium" color="blue.700" mb={3}>
                Opzioni di Importazione
              </Text>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4} wrap="wrap">
                  <FormControl maxW="150px">
                    <FormLabel fontSize="sm">Tipo</FormLabel>
                    <select
                      value={formData.tipo}
                      onChange={(e) =>
                        setFormData({ ...formData, tipo: e.target.value })
                      }
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <option value="cerco">Cerco</option>
                      <option value="scambio">Scambio</option>
                    </select>
                  </FormControl>

                  <FormControl maxW="150px">
                    <FormLabel fontSize="sm">Lingua</FormLabel>
                    <select
                      value={formData.lingua}
                      onChange={(e) =>
                        setFormData({ ...formData, lingua: e.target.value })
                      }
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <option value="it">Italiano</option>
                      <option value="en">Inglese</option>
                      <option value="de">Tedesco</option>
                      <option value="fr">Francese</option>
                      <option value="es">Spagnolo</option>
                    </select>
                  </FormControl>

                  <FormControl maxW="150px">
                    <FormLabel fontSize="sm">Condizioni</FormLabel>
                    <select
                      value={formData.condizioni}
                      onChange={(e) =>
                        setFormData({ ...formData, condizioni: e.target.value })
                      }
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <option value="NM">Near Mint</option>
                      <option value="LP">Lightly Played</option>
                      <option value="MP">Moderately Played</option>
                      <option value="HP">Heavily Played</option>
                      <option value="DMG">Damaged</option>
                    </select>
                  </FormControl>
                </HStack>

                {/* Campo Note per l'importazione bulk */}
                <FormControl>
                  <FormLabel fontSize="sm" color="blue.700">
                    📝 Note per tutte le carte
                  </FormLabel>
                  <Textarea
                    value={formData.note}
                    onChange={(e) =>
                      setFormData({ ...formData, note: e.target.value })
                    }
                    placeholder="Es: 'Importazione bulk da deck Modern', 'Carte per collezione Commander', 'Deck Standard 2024', 'Carte per trading'"
                    rows={3}
                    fontSize="sm"
                    bg="white"
                    borderColor="blue.300"
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                  />
                  <Text fontSize="xs" color="blue.600" mt={1}>
                    💡 Le note verranno applicate a tutte le carte importate
                  </Text>

                  {/* Anteprima delle note */}
                  {formData.note && (
                    <Box
                      mt={2}
                      p={3}
                      bg="green.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color="green.700"
                        mb={2}
                      >
                        📋 Anteprima delle note che verranno generate:
                      </Text>
                      <VStack spacing={1} align="start">
                        <Text fontSize="xs" color="green.600" fontFamily="mono">
                          • {formData.note}
                        </Text>
                      </VStack>
                    </Box>
                  )}
                </FormControl>
              </VStack>
            </Box>

            {/* Pulsante per analizzare */}
            <Box textAlign="center">
              <Button
                colorScheme="blue"
                leftIcon={<FaMagic />}
                onClick={handleParseDeckList}
                isDisabled={!deckList.trim()}
                size="lg"
              >
                Analizza Lista
              </Button>
            </Box>

            {/* Messaggi di successo ed errore */}
            {parseError && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="medium" color="orange.700">
                    Attenzione
                  </Text>
                  <Text fontSize="sm" color="orange.600">
                    {parseError}
                  </Text>
                </Box>
              </Alert>
            )}

            {parseSuccess && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="medium" color="green.700">
                    Successo
                  </Text>
                  <Text fontSize="sm" color="green.600">
                    {parseSuccess}
                  </Text>
                </Box>
              </Alert>
            )}

            {/* Risultato dell'importazione */}
            {importResult && (
              <Alert
                status={importResult.success ? 'success' : 'warning'}
                borderRadius="md"
              >
                <AlertIcon />
                <Box>
                  <Text
                    fontWeight="medium"
                    color={importResult.success ? 'green.700' : 'orange.700'}
                  >
                    Importazione Completata
                  </Text>
                  <Text
                    fontSize="sm"
                    color={importResult.success ? 'green.600' : 'orange.600'}
                  >
                    {importResult.message}
                  </Text>
                </Box>
              </Alert>
            )}

            {/* Messaggi di retry */}
            {retryMessage && (
              <Alert status={retryMessage.type} borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text
                    fontWeight="medium"
                    color={
                      retryMessage.type === 'success'
                        ? 'green.700'
                        : retryMessage.type === 'warning'
                          ? 'orange.700'
                          : 'red.700'
                    }
                  >
                    {retryMessage.title}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={
                      retryMessage.type === 'success'
                        ? 'green.600'
                        : retryMessage.type === 'warning'
                          ? 'orange.600'
                          : 'red.600'
                    }
                  >
                    {retryMessage.message}
                  </Text>
                </Box>
              </Alert>
            )}

            {/* Risultati del parsing */}
            {parsedCards.length > 0 && (
              <>
                <Divider />
                <Box>
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold" color="gray.700">
                      Carte Trovate ({parsedCards.length})
                    </Text>
                    <HStack spacing={2}>
                      <Badge colorScheme="green" variant="subtle">
                        {
                          parsedCards.filter((c) => c.status === 'success')
                            .length
                        }{' '}
                        Successo
                      </Badge>
                      <Badge colorScheme="red" variant="subtle">
                        {parsedCards.filter((c) => c.status === 'error').length}{' '}
                        Errori
                      </Badge>
                      <Badge colorScheme="gray" variant="subtle">
                        {
                          parsedCards.filter((c) => c.status === 'pending')
                            .length
                        }{' '}
                        In attesa
                      </Badge>
                    </HStack>
                  </HStack>

                  {/* Progress bar durante l'importazione */}
                  {importing && (
                    <Box mb={4}>
                      <Progress
                        value={importProgress}
                        colorScheme="blue"
                        size="lg"
                      />
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        mt={2}
                        textAlign="center"
                      >
                        Importazione in corso... {Math.round(importProgress)}%
                      </Text>
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        mt={1}
                        textAlign="center"
                      >
                        🔍 Ricerca automatica su Scryfall per ogni carta...
                      </Text>
                    </Box>
                  )}

                  {/* Lista delle carte */}
                  <Box
                    maxH="300px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={3}
                  >
                    <VStack spacing={2} align="stretch">
                      {parsedCards.map((card, index) => (
                        <Box
                          key={`${card.name}-${card.section}-${card.originalLine}`}
                          p={3}
                          bg={cardBg}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={`${getStatusColor(card.status)}.200`}
                          boxShadow={cardShadow}
                        >
                          <HStack justify="space-between" align="center">
                            <VStack align="start" spacing={1} flex="1">
                              <HStack
                                spacing={2}
                                justify="space-between"
                                align="start"
                                w="100%"
                              >
                                {getStatusIcon(card.status)}
                                <VStack align="start" spacing={1} flex="1">
                                  <Text fontWeight="medium" color="gray.800">
                                    {card.name}
                                  </Text>
                                  <HStack spacing={2}>
                                    {card.setCode && (
                                      <Badge
                                        size="xs"
                                        colorScheme="gray"
                                        variant="outline"
                                      >
                                        {card.setCode}
                                      </Badge>
                                    )}
                                    {card.collectorNumber && (
                                      <Badge
                                        size="xs"
                                        colorScheme="gray"
                                        variant="outline"
                                      >
                                        #{card.collectorNumber}
                                      </Badge>
                                    )}
                                    {card.isFoil && (
                                      <Badge
                                        size="xs"
                                        colorScheme="yellow"
                                        variant="solid"
                                      >
                                        ✨ Foil
                                      </Badge>
                                    )}
                                  </HStack>
                                </VStack>
                                <VStack align="end" spacing={1}>
                                  {card.scryfallFound && (
                                    <Badge
                                      size="sm"
                                      colorScheme={getSearchMethodColor(
                                        card.searchMethod
                                      )}
                                      variant="solid"
                                      leftIcon={<Icon as={FaSearch} />}
                                    >
                                      {getSearchMethodLabel(card.searchMethod)}
                                    </Badge>
                                  )}
                                </VStack>
                              </HStack>
                              {card.error && (
                                <Text fontSize="sm" color="red.600">
                                  Errore: {card.error}
                                </Text>
                              )}
                              {card.errorDetails &&
                                card.errorDetails.length > 0 && (
                                  <VStack align="start" spacing={1} mt={2}>
                                    <Text
                                      fontSize="xs"
                                      color="orange.600"
                                      fontWeight="medium"
                                    >
                                      Dettagli ricerca Scryfall:
                                    </Text>
                                    {card.errorDetails.map((error) => (
                                      <Text
                                        key={`${card.name}-error-${error}`}
                                        fontSize="xs"
                                        color="orange.500"
                                      >
                                        • {error}
                                      </Text>
                                    ))}
                                    <Button
                                      size="xs"
                                      colorScheme="orange"
                                      variant="outline"
                                      onClick={() =>
                                        handleRetryCard(card, index)
                                      }
                                      mt={2}
                                    >
                                      🔄 Riprova Ricerca
                                    </Button>
                                  </VStack>
                                )}
                            </VStack>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              </>
            )}

            {/* Qualità del Match */}
            <VStack spacing={3} align="stretch">
              <Alert status="success" borderRadius="md">
                <Box>
                  <Text fontWeight="medium">Qualità del Match</Text>
                  <VStack spacing={2} align="start" mt={2}>
                    <HStack spacing={2}>
                      <Badge size="sm" colorScheme="green" variant="solid">
                        🎯 Perfetto
                      </Badge>
                      <Text fontSize="xs">
                        Corrispondenza esatta (set + numero)
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Badge size="sm" colorScheme="teal" variant="solid">
                        🎯 Set
                      </Badge>
                      <Text fontSize="xs">Corrispondenza esatta per set</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Badge size="sm" colorScheme="orange" variant="solid">
                        🎯 Nome
                      </Badge>
                      <Text fontSize="xs">Corrispondenza esatta per nome</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Badge size="sm" colorScheme="yellow" variant="solid">
                        🔍 Nome
                      </Badge>
                      <Text fontSize="xs">
                        Ricerca generica (primo risultato)
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </Alert>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={handleClose}>
              Chiudi
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<FaUpload />}
              onClick={handleImportCards}
              isDisabled={
                parsedCards.length === 0 || importing || importCompleted
              }
              isLoading={importing}
              loadingText="Importando..."
            >
              {importCompleted
                ? 'Importazione Completata'
                : `Importa${parsedCards.length > 0 ? ` (${parsedCards.length} carte)` : ''}`}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
